import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  Menu, 
  X, 
  User, 
  LogOut, 
  ChevronDown,
  KeyRound
} from 'lucide-react';
import { logout } from '../../store/slices/authSlice.js';
import { addAlert } from '../../store/slices/uiSlice.js';
import ChangePasswordModal from './ChangePasswordModal.jsx';

const Header = ({ user, onToggleSidebar, onToggleMobileMenu, sidebarCollapsed }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('refreshToken');
    dispatch(logout());
    dispatch(addAlert({
      type: 'success',
      title: 'Sesión Cerrada',
      message: 'Ha cerrado sesión exitosamente',
    }));
    navigate('/login', { replace: true });
  };

  const openPasswordModal = () => {
    setPasswordModalOpen(true);
    setUserMenuOpen(false);
  };

  return (
    <>
      <header className="fixed top-0 right-0 left-0 bg-white border-b border-gray-200 z-30">
        <div className="flex items-center justify-between h-16 px-4">
          {/* Left side - Logo and Menu Toggle */}
          <div className="flex items-center space-x-4">
            {/* Mobile menu button */}
            <button
              onClick={onToggleMobileMenu}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-200"
            >
              <Menu className="h-6 w-6" />
            </button>

            {/* Desktop sidebar toggle */}
            <button
              onClick={onToggleSidebar}
              className="hidden lg:block p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-200"
            >
              {sidebarCollapsed ? <Menu className="h-5 w-5" /> : <X className="h-5 w-5" />}
            </button>

            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                <img src="/ACTrackBird.png" alt="ACTrack Logo" className="w-8 h-8 object-contain" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-primary-900">ACTrack</h1>
                <p className="text-xs text-gray-500">Sistema de Seguimiento</p>
              </div>
            </div>
          </div>

          {/* Right side - User Menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center space-x-3 p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors duration-200"
            >
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-gray-900">
                  {user?.full_name || 'Usuario'}
                </p>
                <p className="text-xs text-gray-500">
                  {user?.role?.role_name || 'Sin rol'}
                </p>
              </div>
              <div className="w-8 h-8 bg-secondary-200 rounded-full flex items-center justify-center">
                <User className="h-5 w-5 text-secondary-600" />
              </div>
              <ChevronDown className="h-4 w-4 text-gray-500" />
            </button>

            {/* User Dropdown Menu */}
            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.full_name || 'Usuario'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {user?.email || 'Sin email'}
                  </p>
                  <p className="text-xs text-primary-600 font-medium">
                    {user?.role?.role_name || 'Sin rol'}
                  </p>
                </div>
                
                <button
                  onClick={openPasswordModal}
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                >
                  <KeyRound className="h-4 w-4 mr-3" />
                  Cambiar Contraseña
                </button>
                
                
                <div className="border-t border-gray-100 mt-1 pt-1">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                  >
                    <LogOut className="h-4 w-4 mr-3" />
                    Cerrar Sesión
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Click outside to close user menu */}
      {userMenuOpen && (
        <div 
          className="fixed inset-0 z-20"
          onClick={() => setUserMenuOpen(false)}
        />
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal 
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
      />
    </>
  );
};

export default Header;